/**
 * `perceivedLatency.helper` — measures the user-perceived "create → ready"
 * latency of a report and emits it as the catalog `perceived_latency`
 * measurement.
 *
 * Feature `telemetry-observability`, task 5.11 (Requirements 12.5, 12.6; design
 * component 9 "Latência percebida create→ready" and Correctness Property P7).
 *
 * ## Boundary — this is the MEASUREMENT helper only
 * This module owns the timing and the at-most-once bookkeeping. The WIRING that
 * feeds it lives in two separate tasks/files that MUST NOT be edited here:
 *
 *  - **Task 5.14** (`~/hooks/useReportCRUD.tsx`): calls
 *    {@link recordCreateIssued} with the report id when the create is issued
 *    (on the `snap:report-created` dispatch).
 *  - **Task 5.15** (`~/components/PendingReportSockets.tsx`): calls
 *    {@link recordCreateCompleted} with the *same* report id when the terminal
 *    `completed` frame is observed on the `snap-status` socket.
 *
 * The public API is deliberately tiny (`recordCreateIssued(id)` /
 * `recordCreateCompleted(id)`) so those wiring sites stay one-liners. Consumers
 * import this file directly (`~/helpers/perceivedLatency.helper`), mirroring how
 * `PendingReportSockets` already imports `reportSaveDedup.helper`.
 *
 * ## Privacy (design components 8/9, Req 14)
 * The report id is used **only** as an in-memory correlation key; it never
 * leaves this module. The emitted payload carries exclusively the closed-set
 * flow tag (`create_to_ready`) and the numeric `durationMs` — no id, no report
 * content, no PII. Emission is delegated to {@link emitEvent}, so it is a safe
 * no-op when Faro is disabled/uninitialized and is sanitized against the
 * per-event attribute allowlist.
 *
 * ## At-most-once + discard-incomplete (Property P7, Req 12.5/12.6)
 * The same report is measured **at most once**. Rather than reimplementing a
 * parallel dedup, this module reuses the existing per-id primitives from
 * `reportSaveDedup.helper` (`shouldIssueSave` / `markSaveIssued`) against a
 * module-scoped registry, mirroring the `issuedReportSavesRef` semantics used
 * by `PendingReportSockets`. A measurement is emitted only when a start was
 * recorded for the id AND the id has not been measured yet. Every other case
 * emits nothing:
 *  - a re-sent `completed` for an already-measured id (poll-cycle re-send);
 *  - a `completed` with no recorded start (start never recorded, socket
 *    drop/reconnect, or the tab closed before the create was timed).
 *
 * Because `performance.now()` is monotonic, the elapsed value can never be
 * negative; a defensive guard drops any non-finite/negative duration without
 * emitting, so no false latency is ever reported.
 *
 * The module is React-free and side-effect-free at import time (it only
 * allocates empty registries), so Property P7 (task 5.21, fast-check) can drive
 * `recordCreateIssued` / `recordCreateCompleted` directly. In-memory state is
 * session-scoped and discarded on tab close; the maps are bounded by the number
 * of distinct report ids seen in the session (a re-created id overwrites its
 * own start rather than adding an entry).
 */

import type { TelemetryEvent } from "~/types/telemetry";
import { markSaveIssued, shouldIssueSave } from "./reportSaveDedup.helper";
import { emitEvent } from "./telemetry.helper";

/** The single flow this helper measures (create issued → terminal ready). */
const CREATE_TO_READY_FLOW = "create_to_ready" as const;

/**
 * Start timestamps (monotonic `performance.now()` ms) keyed by report id,
 * captured when the create is issued. An entry is consumed (deleted) the moment
 * its terminal `completed` is measured, so a lingering entry represents an
 * in-flight — not yet completed — measurement.
 */
const startTimestamps = new Map<string, number>();

/**
 * Registry of report ids already measured, guarded via the shared
 * `reportSaveDedup.helper` primitives. Enforces at-most-once: once an id is
 * here, later `completed` frames for it are ignored.
 */
const measuredReports = new Set<string>();

/** Monotonic clock read, isolated for clarity and to keep a single call site. */
function nowMs(): number {
  return performance.now();
}

/**
 * Records the start of a create→ready measurement for `id`.
 *
 * Called by the create flow (task 5.14) when `snap:report-created` is issued.
 * No-op when `id` is falsy or when the id was already measured (at-most-once:
 * the window stays permanently closed). For a not-yet-measured id the current
 * monotonic timestamp is stored — if called repeatedly before a terminal frame
 * (e.g. a retry that reuses the same `user_reports_id`), the latest create
 * issued wins.
 */
export function recordCreateIssued(id: string | undefined | null): void {
  if (!id) return;
  // Already measured → the create→ready window for this id is permanently
  // closed (P7 at-most-once). Do not reopen it.
  if (!shouldIssueSave(id, measuredReports)) return;
  startTimestamps.set(id, nowMs());
}

/**
 * Closes the create→ready measurement for `id` and, when valid, emits the
 * `perceived_latency` measurement.
 *
 * Called by the socket flow (task 5.15) when the terminal `completed` frame is
 * observed. Emits AT MOST ONE measurement per id and discards every incomplete
 * case — no recorded start, re-sent terminal, or a non-finite/negative
 * duration — without emitting (Req 12.6 / P7).
 */
export function recordCreateCompleted(id: string | undefined | null): void {
  if (!id) return;

  // Re-sent terminal frame for an id already measured → ignore (P7).
  if (!shouldIssueSave(id, measuredReports)) return;

  const start = startTimestamps.get(id);
  if (start === undefined) {
    // No start was ever recorded for this id (start never recorded, or the
    // create happened before telemetry was wired / after a socket
    // drop-reconnect). Discard, and do NOT mark measured so a legitimate later
    // create→completed for this id can still be timed.
    return;
  }

  // Consume the start and close the window for this id (at-most-once): any
  // re-sent `completed` after this point hits the dedup guard above.
  startTimestamps.delete(id);
  markSaveIssued(id, measuredReports);

  const durationMs = Math.round(nowMs() - start);
  // `performance.now()` is monotonic, so this should always hold; guard
  // defensively so no negative/false duration is ever emitted.
  if (!Number.isFinite(durationMs) || durationMs < 0) return;

  const event: TelemetryEvent = {
    name: "perceived_latency",
    flow: CREATE_TO_READY_FLOW,
    durationMs,
  };
  emitEvent(event);
}

/**
 * Clears all in-memory measurement state. Intended for test isolation only
 * (e.g. between fast-check runs of Property P7); production code never needs to
 * call it because the state is session-scoped and discarded on tab close.
 */
export function resetPerceivedLatencyState(): void {
  startTimestamps.clear();
  measuredReports.clear();
}
